import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { User, Phone, MapPin, FileText, Video, RefreshCw, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import './Admin.css';

const STATUS_STYLES = {
    pending:  { background: '#fef9c3', color: '#a16207' },
    reviewed: { background: '#dbeafe', color: '#1d4ed8' },
    accepted: { background: '#dcfce7', color: '#15803d' },
    rejected: { background: '#fee2e2', color: '#dc2626' },
};

const STATUS_LABELS = {
    pending: 'Pending',
    reviewed: 'Reviewed',
    accepted: 'Accepted',
    rejected: 'Rejected',
};

const STATUS_BTN = {
    reviewed: { bg: '#066af2', label: 'Mark Reviewed' },
    accepted: { bg: '#16a34a', label: 'Mark Accepted' },
    rejected: { bg: '#dc2626', label: 'Mark Rejected' },
};

export default function AdminInstructorApplications() {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [updating, setUpdating] = useState(null);
    const [expanded, setExpanded] = useState(null);

    const fetchApplications = async () => {
        setLoading(true);
        setError('');
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const res = await api.get('/instructor-applications', { params });
            setApplications(res.data.applications || []);
        } catch {
            setError('Failed to load applications.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchApplications(); }, [filter]);

    const updateStatus = async (id, status) => {
        setUpdating(id);
        try {
            await api.patch(`/instructor-applications/${id}/status`, { status });
            setApplications(prev => prev.map(a => a._id === id ? { ...a, status } : a));
        } catch {
            alert('Failed to update status.');
        } finally {
            setUpdating(null);
        }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    const FILTERS = ['all', 'pending', 'reviewed', 'accepted', 'rejected'];

    return (
        <div className="admin-container" style={{ paddingTop: '2rem' }}>

            {/* Page Header */}
            <div className="admin-page-header">
                <div className="admin-page-header-text">
                    <h1>Instructor Applications</h1>
                    <p>{applications.length} application{applications.length !== 1 ? 's' : ''}</p>
                </div>
                <button className="btn-action primary" onClick={fetchApplications} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <RefreshCw size={15} /> Refresh
                </button>
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
                {FILTERS.map(s => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        style={{
                            padding: '7px 18px',
                            borderRadius: 8,
                            border: `2px solid ${filter === s ? '#066af2' : '#e2e8f0'}`,
                            background: filter === s ? '#066af2' : '#fff',
                            color: filter === s ? '#fff' : '#64748b',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            transition: 'all 0.15s'
                        }}
                    >
                        {s === 'all' ? 'All' : STATUS_LABELS[s]}
                    </button>
                ))}
            </div>

            {error && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontWeight: 600 }}>
                    {error}
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b', fontSize: 16 }}>Loading applications...</div>
            ) : applications.length === 0 ? (
                <div className="admin-table-section-card" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    <FileText size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                    <p style={{ margin: 0, fontWeight: 600 }}>No applications found.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {applications.map(app => (
                        <div
                            key={app._id}
                            className="admin-table-section-card"
                            style={{ padding: 0, overflow: 'hidden' }}
                        >
                            {/* Card header row */}
                            <div
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', cursor: 'pointer', gap: 12 }}
                                onClick={() => setExpanded(expanded === app._id ? null : app._id)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg,#066af2,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <User size={18} color="#fff" />
                                    </div>
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{app.fullName}</p>
                                        <p style={{ margin: 0, color: '#64748b', fontSize: 12 }}>{app.email} · {formatDate(app.submittedAt)}</p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{
                                        padding: '4px 12px', borderRadius: 9999, fontSize: 11, fontWeight: 800,
                                        textTransform: 'uppercase', letterSpacing: '0.05em',
                                        ...STATUS_STYLES[app.status]
                                    }}>
                                        {STATUS_LABELS[app.status]}
                                    </span>
                                    {expanded === app._id
                                        ? <ChevronUp size={18} color="#94a3b8" />
                                        : <ChevronDown size={18} color="#94a3b8" />
                                    }
                                </div>
                            </div>

                            {/* Expanded details */}
                            {expanded === app._id && (
                                <div style={{ borderTop: '1px solid #f1f5f9', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                                        <InfoRow icon={<Phone size={14} />} label="Phone" value={app.phone} />
                                        <InfoRow icon={<MapPin size={14} />} label="Location" value={app.location} />
                                        <InfoRow
                                            icon={<FileText size={14} />}
                                            label="CV"
                                            value={<a href={app.cvLink} target="_blank" rel="noreferrer" style={{ color: '#066af2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>Open CV <ExternalLink size={12} /></a>}
                                        />
                                        <InfoRow
                                            icon={<Video size={14} />}
                                            label="Intro Video"
                                            value={<a href={app.introVideoLink} target="_blank" rel="noreferrer" style={{ color: '#066af2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>Watch <ExternalLink size={12} /></a>}
                                        />
                                        <InfoRow
                                            icon={<Video size={14} />}
                                            label="2nd Video"
                                            value={<a href={app.secondVideoLink} target="_blank" rel="noreferrer" style={{ color: '#066af2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>Watch <ExternalLink size={12} /></a>}
                                        />
                                    </div>

                                    {/* Action buttons */}
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4, borderTop: '1px solid #f1f5f9' }}>
                                        {Object.entries(STATUS_BTN).map(([s, { bg, label }]) => (
                                            <button
                                                key={s}
                                                disabled={app.status === s || updating === app._id}
                                                onClick={() => updateStatus(app._id, s)}
                                                style={{
                                                    padding: '8px 18px', borderRadius: 8, border: 'none',
                                                    cursor: app.status === s ? 'default' : 'pointer',
                                                    background: bg, color: '#fff',
                                                    fontSize: 13, fontWeight: 700,
                                                    opacity: app.status === s ? 0.35 : 1,
                                                    transition: 'opacity 0.15s'
                                                }}
                                            >
                                                {updating === app._id ? '...' : label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function InfoRow({ icon, label, value }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#94a3b8', flexShrink: 0 }}>{icon}</span>
            <span style={{ color: '#64748b', fontSize: 12, fontWeight: 600, minWidth: 70 }}>{label}:</span>
            <span style={{ color: '#0f172a', fontSize: 13 }}>{value}</span>
        </div>
    );
}
